import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  connectedCallback() {
    super.connectedCallback();
    // During morphSection, connectedCallback fires before the parent tr is fully
    // populated — .cart-items__properties may not exist yet. Re-run after the
    // current paint so the full DOM is guaranteed to be in place.
    requestAnimationFrame(() => this.updateButtonStates());
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits.
   * Cart buttons are always managed client-side, never server-disabled.
   * For Limited Edition items (identified via .cart-items__properties in the
   * same tr), both buttons are kept permanently disabled.
   */
  isWineVariant(row) {
    const variantId = row?.dataset?.variantId;
    if (!variantId) return false;
    try {
      const rules = JSON.parse(sessionStorage.getItem('wineUpsellRules') || '{}');
      return !!rules[variantId];
    } catch (e) {
      return false;
    }
  }

  showQtyText(row, input) {
    if (!row || row.dataset.qtyTextShown) return;
    row.dataset.qtyTextShown = '1';
    const controls = row.querySelector('.cart-items__quantity-controls');
    if (!controls) return;
    const qty = input?.value || '1';
    const label = document.createElement('span');
    label.className = 'cart-qty-fixed-label';
    label.textContent = 'Quantity: ' + qty;
    controls.prepend(label);
  }

  updateButtonStates() {
    const { minusButton, plusButton, quantityInput } = this.refs;
    const row = this.closest('tr.cart-items__table-row');

    // Limited Edition: disable buttons and show quantity as text
    const propsEl = row?.querySelector('.cart-items__properties');
    if (propsEl && propsEl.textContent.indexOf('Limited Edition Number') !== -1) {
      row.setAttribute('data-limited-edition', '');
      minusButton.disabled = true;
      plusButton.disabled = true;
      this.showQtyText(row, quantityInput);
      return;
    }

    // Wine upsell: disable buttons and show quantity as text
    if (this.isWineVariant(row)) {
      row.setAttribute('data-wine-cart', '');
      minusButton.disabled = true;
      plusButton.disabled = true;
      this.showQtyText(row, quantityInput);
      return;
    }

    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}